import axios from 'axios'
import Cookies from 'js-cookie'

import { FILTER_NAME, SELECTION_METHOD } from 'Pods/SearchPage/Shared/Tracking/constants'
import { trackFilterSelect } from 'Pods/SearchPage/Shared/Tracking/searchPageTracker'
import { ASSET_TYPE } from 'Pods/SearchPage/Store/constants'

// Both are read by ListingPageController#redirect_to_remembered_asset_type: it adds the remembered
// type to listing URLs that carry none, and sets the applied flag for the redirected page to report.
export const REMEMBERED_ASSET_TYPE_COOKIE = 'search_asset_type'
export const REMEMBERED_ASSET_TYPE_APPLIED_COOKIE = 'search_asset_type_applied'

const REMEMBERABLE_ASSET_TYPES = [ASSET_TYPE.THREE_D_ONLY, ASSET_TYPE.THREE_D_PRINT_ONLY]
const EXPIRES_DAYS = 365
const ACCOUNT_PATH = '/api/internal/profile/search-asset-type'

const isRememberable = (assetType) => REMEMBERABLE_ASSET_TYPES.includes(assetType)

// Removal must repeat the path or the browser keeps the original cookie.
const COOKIE_SCOPE = { path: '/' }

const writeCookie = (assetType) => {
  Cookies.set(REMEMBERED_ASSET_TYPE_COOKIE, assetType, {
    ...COOKIE_SCOPE,
    expires: EXPIRES_DAYS,
    sameSite: 'lax',
    ...(window.location.protocol === 'https:' && { secure: true }),
  })
}

const csrfToken = () => document.getElementsByName('csrf-token')[0]?.getAttribute('content')

const saveToAccount = (assetType) => {
  const config = { headers: { 'X-CSRF-Token': csrfToken() }, withCredentials: true }
  const request = isRememberable(assetType)
    ? axios.put(ACCOUNT_PATH, { asset_type: assetType }, config)
    : axios.delete(ACCOUNT_PATH, config)

  // A failed save only loses the cross-device copy: this browser's cookie already holds the choice.
  request.catch(() => {})
}

/**
 * Remembers an explicit asset type choice (type toggle or quick filter shortcut); "All" forgets it.
 * Signed-in viewers also get it saved on their account.
 * @param {string} assetType - One of ASSET_TYPE
 */
export const rememberAssetType = (assetType) => {
  if (isRememberable(assetType)) {
    writeCookie(assetType)
  } else {
    Cookies.remove(REMEMBERED_ASSET_TYPE_COOKIE, COOKIE_SCOPE)
  }

  if (window.pageConfig?.currentUserId) {
    saveToAccount(assetType)
  }
}

/**
 * Copies the account's remembered type into a browser that has no cookie yet. An existing cookie
 * (e.g. chosen as a guest before signing in) wins until the next explicit choice writes both.
 * The redirect applies it from the next page view; the current page keeps its type.
 * @param {string} [accountAssetType] - pageConfig.searchAssetType
 */
export const adoptAccountAssetType = (accountAssetType) => {
  if (!isRememberable(accountAssetType) || Cookies.get(REMEMBERED_ASSET_TYPE_COOKIE)) return

  writeCookie(accountAssetType)
}

/**
 * Reports, once, the type the server redirect applied to this page load.
 */
export const reportAppliedAssetType = () => {
  const appliedAssetType = Cookies.get(REMEMBERED_ASSET_TYPE_APPLIED_COOKIE)
  if (!appliedAssetType) return

  Cookies.remove(REMEMBERED_ASSET_TYPE_APPLIED_COOKIE, COOKIE_SCOPE)
  if (!isRememberable(appliedAssetType)) return

  trackFilterSelect({
    name: FILTER_NAME.PRODUCT_TYPE_TOGGLE,
    value: appliedAssetType,
    selectionMethod: SELECTION_METHOD.REMEMBERED,
  })
}
